import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import Button3D from '../ui/Button3D';
import Modal3D from '../ui/Modal3D';
import AdminAuthenticatorCard from '../auth/AdminAuthenticatorCard';
import { listMyCompanies } from '../../lib/activeCompany';
import { enterCompany } from '../../lib/companyChoice';
import '../../styles/companySwitcher.css';
import { clearModuleModesCache } from '../../hooks/useModuleModes';

// BL-408 G3: the top-bar company switcher. Works like Microsoft Teams: it always names the company you are in, lists only
// your own companies (mcs_my_companies), and switching asks the server (mcs_set_active_company), then reloads cleanly so no
// screen, cache or live subscription from the old company survives.
// G4 (Val, 2026-10-24: "not present"): it now shows for EVERY signed-in staff person. With one company it names that company
// and its menu says it is their only one (nothing to switch). Platform role holders also get a "Platform" section linking
// to the platform pages (Support Console, Platform Roles) whatever their company count; where they land after sign-in is
// unchanged (Q-022). If the list cannot be read it shows nothing (or only the Platform section for platform people).
// Brands inside a company (VWH -> The Ride ATL) are not here; the brand picker in the account menu is unchanged.
export const PLATFORM_MENU_LINKS = Object.freeze([
    { path: '/support-console', label: 'Support Console' },
    { path: '/platform-roles', label: 'Platform Roles' },
]);

const FAILURE_TEXT = {
    not_available: 'That company is not available to you any more.',
    failed: 'That company could not be opened right now. Try again.',
};

// UX pass 2026-10-25: the list-and-switch logic, shared by this top-bar switcher and the account menu's company section
// (Val: "I feel this is where the company switcher should be located"). Same data (mcs_my_companies), same server switch
// (mcs_set_active_company via enterCompany), same clean reload. `open`/`setOpen` belong to whichever menu shows it.
export function useCompanySwitch({ client, userId, landingPath = '/', onSignOut, reload, setOpen = () => {} }) {
    const [companies, setCompanies] = useState([]);
    const [busyId, setBusyId] = useState(null);
    const [error, setError] = useState('');
    const [mfaTarget, setMfaTarget] = useState(null);

    const refresh = useCallback(async () => {
        try {
            setCompanies(await listMyCompanies(client));
        } catch (failure) {
            // Fail quiet: the switcher simply does not show. The server still decides the company on every request.
            setCompanies([]);
        }
    }, [client]);

    useEffect(() => {
        if (!userId || !client) { setCompanies([]); return undefined; }
        let live = true;
        listMyCompanies(client).then((list) => { if (live) setCompanies(list); }).catch(() => { if (live) setCompanies([]); });
        return () => { live = false; };
    }, [client, userId]);

    async function switchTo(company) {
        if (company.isActive) { setOpen(false); return; }
        setBusyId(company.orgId);
        setError('');
        let result;
        try {
            result = await enterCompany(client, company, {
                landingPath,
                beforeReload: () => { clearModuleModesCache(); return client.removeAllChannels?.(); },
                ...(reload ? { reload } : {}),
            });
        } catch (failure) {
            result = { ok: false, reason: 'failed' };
        }
        if (result.ok) return;
        setBusyId(null);
        if (result.reason === 'mfa_required') {
            setOpen(false);
            setMfaTarget(company);
        } else if (result.reason === 'sign_in_again') {
            onSignOut?.();
        } else {
            setError(FAILURE_TEXT[result.reason] || FAILURE_TEXT.failed);
            refresh();
        }
    }

    return { companies, busyId, error, setError, mfaTarget, setMfaTarget, switchTo };
}

// The authenticator step some companies ask for before they open (same dialog for both entry points).
export function CompanySwitchMfa({ client, theme, onSignOut, mfaTarget, setMfaTarget, switchTo }) {
    return (
        <>
            {mfaTarget && (
                <Modal3D
                    title={`Open ${mfaTarget.name}`}
                    subtitle="This company asks for a code from your authenticator."
                    stripe="gold"
                    theme={theme}
                    portal
                    testId="company-switch-mfa"
                    onClose={() => setMfaTarget(null)}
                >
                    <AdminAuthenticatorCard
                        client={client}
                        onDone={async () => { const target = mfaTarget; setMfaTarget(null); await switchTo(target); }}
                        onSignOut={onSignOut}
                    />
                </Modal3D>
            )}
        </>
    );
}

export default function CompanySwitcher({
    client, userId, landingPath = '/', onSignOut, theme = 'dark', reload, isPlatform = false, onOpenPlatformLink,
}) {
    const [open, setOpen] = useState(false);
    const menuId = useId();
    const rootRef = useRef(null);
    const buttonRef = useRef(null);
    const { companies, busyId, error, setError, mfaTarget, setMfaTarget, switchTo } = useCompanySwitch({
        client, userId, landingPath, onSignOut, reload, setOpen,
    });

    useEffect(() => {
        if (!open) return undefined;
        const onPointer = (event) => { if (rootRef.current && !rootRef.current.contains(event.target)) setOpen(false); };
        const onKey = (event) => {
            if (event.key === 'Escape') { setOpen(false); buttonRef.current?.focus(); }
        };
        document.addEventListener('pointerdown', onPointer);
        document.addEventListener('keydown', onKey);
        return () => { document.removeEventListener('pointerdown', onPointer); document.removeEventListener('keydown', onKey); };
    }, [open]);

    const platform = Boolean(isPlatform && onOpenPlatformLink);
    if (companies.length === 0 && !platform) return null;
    const several = companies.length >= 2;
    const current = companies.find((company) => company.isActive) || (companies.length === 1 ? companies[0] : null);
    const label = current ? current.name : several ? 'Choose a company' : 'Platform';
    const ariaLabel = several
        ? `Company: ${current ? current.name : 'none chosen'}. Switch company`
        : current ? `Company: ${current.name}. Company menu` : 'Platform menu';

    return (
        <div className="ia-company-switcher" ref={rootRef}>
            <Button3D
                ref={buttonRef}
                variant="pill"
                className="ia-company-switcher__button"
                aria-haspopup="true"
                aria-expanded={open}
                aria-controls={menuId}
                aria-label={ariaLabel}
                onClick={() => { setError(''); setOpen((value) => !value); }}
            >
                <span className="ia-company-switcher__label">{label}</span>
                <span aria-hidden="true" className="ia-company-switcher__caret">▾</span>
            </Button3D>
            {open && (
                <div id={menuId} className="ia-company-switcher__menu" data-theme={theme} role="group"
                    aria-label={several ? 'Switch company' : 'Company menu'}>
                    {companies.length > 0 && <p className="ia-company-switcher__title">{several ? 'Switch company' : 'Your company'}</p>}
                    {companies.length > 0 && <ul className="ia-company-switcher__list">
                        {companies.map((company) => (
                            <li key={company.orgId}>
                                <button
                                    type="button"
                                    className="ia-company-switcher__item"
                                    aria-current={company.isActive ? 'true' : undefined}
                                    disabled={Boolean(busyId) || company.canEnter === false}
                                    onClick={() => switchTo(company)}
                                >
                                    <span className="ia-company-switcher__name">{company.name}</span>
                                    <span className="ia-company-switcher__state">
                                        {company.isActive ? 'Current' : busyId === company.orgId ? 'Opening…' : company.canEnter === false ? 'Not available' : ''}
                                    </span>
                                </button>
                            </li>
                        ))}
                    </ul>}
                    {companies.length === 1 && (
                        <p className="ia-company-switcher__note">This is your only company. If you are added to another one, you can switch here.</p>
                    )}
                    {error && <p role="alert" className="ia-company-switcher__error">{error}</p>}
                    {platform && (
                        <div className="ia-company-switcher__platform">
                            <p className="ia-company-switcher__title">Platform</p>
                            <ul className="ia-company-switcher__list">
                                {PLATFORM_MENU_LINKS.map((link) => (
                                    <li key={link.path}>
                                        <button type="button" className="ia-company-switcher__item" disabled={Boolean(busyId)}
                                            onClick={() => { setOpen(false); onOpenPlatformLink(link.path); }}>
                                            <span className="ia-company-switcher__name">{link.label}</span>
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>
            )}
            <CompanySwitchMfa client={client} theme={theme} onSignOut={onSignOut} mfaTarget={mfaTarget} setMfaTarget={setMfaTarget} switchTo={switchTo} />
        </div>
    );
}
